import { useState } from 'react';
import Moment from 'react-moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useHistory } from 'react-router-dom';
import Loading from '../../common/loading';
import Table from '../../common/table';
import { Urls } from '../../../config/urls';
import l from '../../../lang';
import './styles.scss';
import TransactionStatusIcon from '../../common/transaction-status-icon';
import { createDate, getMappedCountryCode, formatAmount } from '../../../utils/index';
import { Routes } from '../../../config/routes';
import TopDashboardSection from '../../common/top-dashboard-section';
import ButtonMore from '../../common/button-more';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import { Empty, EmptyNoFound } from './empty';
import TransactionFilter from './filter';
import { useMediaQuery } from '@mui/material';
import ExportTransactions from './export';
import { useTransactions } from '../../../store/transactions';
import { Filters } from '../../../store/interfaces';
import AlertMessages from '../alert-messages';
import { segmentTransactionsTransactionDetail } from '../../../utils/analytics';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import { TransactionTooltipButton } from './transaction-tooltip';
import { getEciBrandResult, ECI_RESULT, isTransactionRefunded } from '../../../utils/transactions-utils';
import { PaymentStatus } from '../../../constants';
import { ReactComponent as CopyIcon } from '../../../assets/icons/copy.svg';
import { ReactComponent as ChargebackIcon } from '../../../assets/icons/card.svg';
import { ReactComponent as DeclinedWarningIcon } from '../../../assets/icons/warning-alert.svg';
import { useTransactionSearchParams, TransactionFilters } from '../../../hooks/use-transaction-search-params';

const Transactions = () => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const { loading, error, allTransactions } = useTransactions();
  const { searchState, setFilters, setPage, setPerPage, clearFilters, hasActiveFilters } = useTransactionSearchParams();

  const history = useHistory();
  const [copiedInvoiceId, setCopiedInvoiceId] = useState<string | null>(null);
  const [copiedEmailInvoiceId, setCopiedEmailInvoiceId] = useState<string | null>(null);

  /**
   * Maps hook's TransactionFilters (snake_case keys) to the Filters dict
   * expected by TransactionFilter component (camelCase key: paymentMethodId).
   */
  const toComponentFilters = (filters: TransactionFilters): Filters => ({
    search_by: filters.search_by,
    status: filters.status,
    country: filters.country,
    currency: filters.currency,
    from: filters.from,
    to: filters.to,
    paymentMethodId: filters.payment_method_id,
  });

  /**
   * Maps Filters dict from TransactionFilter component (camelCase paymentMethodId)
   * back to TransactionFilters (snake_case payment_method_id) for the hook.
   */
  const fromComponentFilters = (filters: Filters): Partial<TransactionFilters> => ({
    search_by: filters.search_by ?? '',
    status: filters.status ?? '',
    country: filters.country ?? '',
    currency: filters.currency ?? '',
    from: filters.from ?? '',
    to: filters.to ?? '',
    payment_method_id: filters.paymentMethodId ?? '',
    transaction_id: filters.transaction_id ?? '',
    external_id: filters.external_id ?? '',
    client_name: filters.client_name ?? '',
    client_email: filters.client_email ?? '',
    tx_email: filters.tx_email ?? '',
    document: filters.document ?? '',
    total_amount: filters.total_amount ?? '',
  });

  const hasTransactions = allTransactions && allTransactions?.data?.length > 0;

  const usesDetailActionButton = (status?: PaymentStatus) =>
    status === PaymentStatus.COMPLETED || status === PaymentStatus.DECLINED;

  const renderCompletedDate = (status: PaymentStatus, creationDate: string) => {
    if (status === PaymentStatus.COMPLETED) {
      return <Moment format="DD/MM/YY">{createDate(creationDate)}</Moment>;
    }

    return <span>---</span>;
  };

  const EmptyBody = () => {
    if (!error && !hasTransactions && !hasActiveFilters) {
      return <Empty />;
    }
    if (error || !hasTransactions) {
      return <EmptyNoFound />;
    }
    return null;
  };

  return (
    <div className="transactions">
      <TopDashboardSection title={l('menu.item.TRANSACTIONS')} subtitle={l('transactions.list.subheadline')} />
      <AlertMessages />
      <Card>
        <CardDescription>
          <div className="transactions_header">
            <span>{l('transactions.subheadline')}</span>
            <div className={isDesktop ? '' : 'transactions__export__btn'}>{<ExportTransactions />}</div>
          </div>
        </CardDescription>
        <div className="transactions__content">
          <div className="transactions__filter_wrapper">
            <TransactionFilter
              loading={loading}
              appliedFilters={toComponentFilters(searchState.filters)}
              clearFilters={clearFilters}
              changeFilters={filters => setFilters(fromComponentFilters(filters))}
            />
          </div>
          {loading && (
            <div className="transactions__loading">
              <Loading />
            </div>
          )}
          {!loading && (
            <div className="transactions__table">
              <Table
                withBtnMenu
                withoutEdgeSpacers
                cellAlign="left"
                emptyBody={<EmptyBody />}
                headers={[
                  <span className="transactions__column__left">{l('transactions.createDate')}</span>,
                  <span className="transactions__column__left">{l('clients.completedDate')}</span>,
                  <span className="transactions__column__left">{l('clients.copyId')}</span>,
                  <span className="transactions__column__left">{l('transactions.country')}</span>,
                  <span className="transactions__column__left">{l('transactions.name')}</span>,
                  <span className="transactions__column__left">{l('clients.email')}</span>,
                  <span className="transactions__column__left">{l('transactions.paymentMethod')}</span>,
                  <span className="transactions__column__right transactions__title">{l('transactions.amountPaid')}</span>,
                  <span className="transactions__column__right transactions__title">
                    {l('transactions.amountReceived')}
                  </span>,
                  <span>{l('transactions.status')}</span>,
                ]}
                body={
                  (hasTransactions &&
                    !error &&
                    allTransactions?.data?.map(trans => {
                      const eciResult = getEciBrandResult(
                        trans.checkoutPaymentMethodCode,
                        trans.errorLogEciCode
                      );
                      const showReviewStatusForTxDetails =
                        trans.status === PaymentStatus.CANCELLED && eciResult === ECI_RESULT.SUCCESS;

                      const statusText = l(
                        trans.status
                          ? `transactions.status.${showReviewStatusForTxDetails ? `DECLINED` : trans.status ?? `DECLINED`}`
                          : `transactions.status.DECLINED`
                      );

                      const displayStatus =
                        eciResult === ECI_RESULT.FAILURE ? `${statusText} (3DS)` : statusText;

                      const transactionCountry = trans.country ?? trans.checkoutCountry;
                      const transactionCountryCode = getMappedCountryCode(transactionCountry);
                      const transactionEmail = trans.clientEmail ?? trans.txEmail ?? '';
                      const paymentMethodCode = trans.checkoutPaymentMethodCode;

                      return [
                        <span className="transactions__column__left">
                          <Moment format="DD/MM/YY">{createDate(trans.creationDate)}</Moment>
                        </span>,
                        <span className="transactions__column__left">
                          {renderCompletedDate(trans.status, trans.creationDate)}
                        </span>,
                        <div className="transactions__copy-cell">
                          {trans.externalId && (
                          <CopyToClipboard
                            text={trans.externalId}
                            onCopy={() => setCopiedInvoiceId(trans.externalId)}
                          >
                            <Tooltip
                              onMouseEnter={() => setCopiedInvoiceId(null)}
                              title={l(copiedInvoiceId === trans.externalId ? 'common.copied' : 'common.copy')}
                            >
                              <button type="button" className="transactions__copy-link">
                                <CopyIcon className="transactions__copy-link-icon" aria-hidden />
                                <span>{l('clients.copyId')}</span>
                              </button>
                            </Tooltip>
                          </CopyToClipboard>
                          )}
                          {trans.preChargebackForm?.comments && (
                            <Tooltip title={l('transactions.client_sent_chargeback_form')}>
                              <ErrorOutlineIcon className="transactions__column__invoice__icon" />
                            </Tooltip>
                          )}
                        </div>,
                        <div className="transactions__country">
                          {transactionCountry ? (
                            <>
                              <img
                                alt=""
                                className="transactions__country__flag country-flag--rounded"
                                src={`${Urls.roundedFlags.replace(':code', transactionCountryCode)}`}
                              />
                              <span>{transactionCountryCode}</span>
                            </>
                          ) : (
                            <span>-</span>
                          )}
                        </div>,
                        <span className="transactions__column__left transactions__client-name-cell">
                          {trans.clientFirstName ?? trans.checkoutClientFirstName}{' '}
                          {trans.clientLastName ?? trans.checkoutClientLastName}
                        </span>,
                        <div className="transactions__email-cell">
                          {transactionEmail ? (
                            <CopyToClipboard
                              text={transactionEmail}
                              onCopy={() => setCopiedEmailInvoiceId(trans.invoiceId)}
                            >
                              <Tooltip
                                onMouseEnter={() => setCopiedEmailInvoiceId(null)}
                                title={l(
                                  copiedEmailInvoiceId === trans.invoiceId ? 'common.copied' : 'common.copy'
                                )}
                              >
                                <button type="button" className="transactions__copy-link">
                                  <CopyIcon className="transactions__copy-link-icon" aria-hidden />
                                  <span>{l('clients.email')}</span>
                                </button>
                              </Tooltip>
                            </CopyToClipboard>
                          ) : (
                            <span>-</span>
                          )}
                        </div>,
                        <div className="transactions__payment_method">
                          {paymentMethodCode ? (
                            <Tooltip title={trans.paymentMethod ?? trans.checkoutPaymentMethod ?? ''}>
                              <img
                                alt={trans.paymentMethod ?? trans.checkoutPaymentMethod ?? ''}
                                className="transactions__payment_method__flag"
                                src={`${Urls.paymentMethodsWide.replace(':code', paymentMethodCode)}`}
                              />
                            </Tooltip>
                          ) : (
                            <span className="transactions__column__left">
                              {trans.paymentMethod ?? trans.checkoutPaymentMethod ?? '-'}
                            </span>
                          )}
                        </div>,
                        <span className="transactions__column__right">
                          {trans.currency ?? trans.checkoutLocalCurrency} {formatAmount(trans.localAmount ?? trans.checkoutLocalAmount)}
                        </span>,
                        <span className="transactions__column__right">
                          {trans.balanceCurrency ?? ''} {trans.balanceAmount != null ? formatAmount(trans.balanceAmount) : '-'}
                        </span>,
                        <div className="transactions__status-cell">
                          {isTransactionRefunded(trans) ? (
                            <>
                              <TransactionStatusIcon
                                status="COMPLETED"
                                tooltipTitle={displayStatus}
                              />
                              <TransactionStatusIcon
                                status="REFUNDED"
                                tooltipTitle={l('transactions.status.REFUNDED')}
                              />
                            </>
                          ) : (
                            <TransactionStatusIcon
                              status={
                                showReviewStatusForTxDetails
                                  ? 'DECLINED'
                                  : trans.status ?? 'DECLINED'
                              }
                              tooltipTitle={displayStatus}
                            />
                          )}
                          {!trans.country && !showReviewStatusForTxDetails && (
                            <Tooltip
                              title={l('transactions.declined.fixTooltip')}
                              arrow
                              placement="top"
                              classes={{
                                tooltip: 'transactions__chargeback-tooltip',
                                arrow: 'transactions__chargeback-tooltip__arrow',
                              }}
                            >
                              <span
                                className="transactions__declined-warning-icon-wrap"
                                tabIndex={0}
                                aria-label={l('transactions.declined.fixTooltip')}
                              >
                                <DeclinedWarningIcon className="transactions__declined-warning-icon" aria-hidden />
                              </span>
                            </Tooltip>
                          )}
                          {trans.chargeback?.externalId && (
                            <Tooltip
                              title={l('transactions.chargeback.iconTooltip')}
                              arrow
                              placement="top"
                              classes={{
                                tooltip: 'transactions__chargeback-tooltip',
                                arrow: 'transactions__chargeback-tooltip__arrow',
                              }}
                            >
                              <span
                                className="transactions__chargeback-icon-wrap"
                                tabIndex={0}
                                aria-label={l('transactions.chargeback.iconTooltip')}
                              >
                                <ChargebackIcon className="transactions__chargeback-icon" aria-hidden />
                              </span>
                            </Tooltip>
                          )}
                        </div>,
                        <div className="transactions__actions-cell">
                          {trans.country || showReviewStatusForTxDetails ? (
                            <ButtonMore
                              variant="detail"
                              onClick={() => {
                                segmentTransactionsTransactionDetail({
                                  externalId: trans.externalId,
                                });
                                history.push(Routes.TRANSACTION_DETAIL.replace(':invoice_id', trans.invoiceId));
                              }}
                            />
                          ) : (
                            <TransactionTooltipButton
                              transactionId={trans.invoiceId}
                              type={trans.endUserErrorType}
                              document={trans.errorLogDocument}
                              documentType={trans.errorLogDocumentType}
                              email={trans.errorLogEmail}
                            />
                          )}
                        </div>,
                      ];
                    })) ||
                  null
                }
                rowsPerPageOptions={[10, 25, 50]}
                rowsLength={allTransactions?.totalElements}
                rowsPerPage={allTransactions?.size ?? 10}
                page={allTransactions?.page}
                handleChangePage={(event, newPage) => {
                  setPage(newPage + 1);
                }}
                handleChangeRowsPerPage={event => {
                  setPerPage(Number(event.target.value));
                }}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Transactions;
